import { resolveProvinceAdcodeLoose } from '../components/Map/provinceAdcode';
import { DEFAULT_POLICY_FILTER, type Policy, type PolicyFilter, type PolicySortBy } from '../types';

const ZH_LOCALE = 'zh-Hans-CN';

function normalizeDateValue(value: string | undefined): string {
  return (value ?? '').trim();
}

function dateToComparableNumber(value: string): number {
  const raw = normalizeDateValue(value);
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function includesKeyword(parts: Array<string | undefined>, keyword: string): boolean {
  const needle = keyword.trim().toLowerCase();
  if (!needle) return true;
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

function computeRelevance(policy: Policy, keyword: string): number {
  const needle = keyword.trim().toLowerCase();
  if (!needle) return 0;

  let score = 0;
  const title = policy.title.toLowerCase();
  if (title.includes(needle)) score += 10;
  if (policy.summary.toLowerCase().includes(needle)) score += 5;
  if (policy.issuingOffice.toLowerCase().includes(needle)) score += 3;
  if (
    [policy.province, policy.city, policy.parkName]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle)
  ) {
    score += 2;
  }
  if (policy.keywords.some((item) => item.toLowerCase().includes(needle))) score += 4;
  if (policy.industryTags.some((item) => item.toLowerCase().includes(needle))) score += 3;
  if (policy.supportTypes.some((item) => item.toLowerCase().includes(needle))) score += 3;
  if (policy.targetScopes.some((item) => item.toLowerCase().includes(needle))) score += 2;
  return score;
}

export function countPoliciesByLevel(policies: Policy[]): {
  total: number;
  national: number;
  provincial: number;
  city: number;
  park: number;
  local: number;
} {
  const national = policies.filter((policy) => policy.level === 'national').length;
  const provincial = policies.filter((policy) => policy.level === 'provincial').length;
  const city = policies.filter((policy) => policy.level === 'city').length;
  const park = policies.filter((policy) => policy.level === 'park').length;
  return {
    total: policies.length,
    national,
    provincial,
    city,
    park,
    local: provincial + city + park,
  };
}

export function getUniquePolicyFieldValues(
  policies: readonly Policy[],
  field: 'province' | 'city' | 'issuingOffice',
): string[] {
  const values = new Set<string>();
  for (const policy of policies) {
    const value = policy[field].trim();
    if (value) values.add(value);
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b, ZH_LOCALE));
}

/** 侧栏未设置任何有效筛选（与默认条件等效）时，列表点选仅打开详情、不联动地图下钻 */
export function isPolicyFilterAtDefault(filter: PolicyFilter): boolean {
  const d = DEFAULT_POLICY_FILTER;
  return (
    !filter.keyword?.trim() &&
    filter.level === d.level &&
    !filter.province?.trim() &&
    !filter.city?.trim() &&
    !filter.issuingOffice?.trim() &&
    !filter.startDate?.trim() &&
    !filter.endDate?.trim() &&
    filter.sortBy === d.sortBy
  );
}

export function filterPolicies(policies: Policy[], filter: PolicyFilter): Policy[] {
  const keyword = filter.keyword.trim();
  return policies.filter((policy) => {
    if (filter.level !== 'all' && policy.level !== filter.level) {
      return false;
    }
    if (filter.province && policy.province !== filter.province) {
      return false;
    }
    if (filter.city && policy.city !== filter.city && policy.parkName !== filter.city) {
      return false;
    }
    if (filter.issuingOffice && policy.issuingOffice !== filter.issuingOffice) {
      return false;
    }
    const issuedAtNumber = dateToComparableNumber(policy.issuedAt);
    const startDateNumber = dateToComparableNumber(filter.startDate ?? '');
    const endDateNumber = dateToComparableNumber(filter.endDate ?? '');
    const hasDateRangeFilter = Boolean(startDateNumber || endDateNumber);

    if (hasDateRangeFilter && !issuedAtNumber) {
      return false;
    }

    if (startDateNumber && issuedAtNumber < startDateNumber) {
      return false;
    }
    if (endDateNumber && issuedAtNumber > endDateNumber) {
      return false;
    }

    if (
      keyword &&
      !includesKeyword(
        [
          policy.title,
          policy.province,
          policy.city,
          policy.parkName,
          policy.issuingOffice,
          policy.summary,
          ...policy.targetScopes,
          ...policy.industryTags,
          ...policy.supportTypes,
          ...policy.keywords,
        ],
        keyword,
      )
    ) {
      return false;
    }

    return true;
  });
}

export function sortPolicies(
  policies: Policy[],
  sortBy: PolicySortBy,
  keyword: string,
): Policy[] {
  const levelWeight: Record<Policy['level'], number> = {
    national: 0,
    provincial: 1,
    city: 2,
    park: 3,
  };

  return [...policies].sort((a, b) => {
    if (sortBy === 'levelThenIssuedAt') {
      const levelDiff = levelWeight[a.level] - levelWeight[b.level];
      if (levelDiff !== 0) return levelDiff;

      const aDate = dateToComparableNumber(a.issuedAt);
      const bDate = dateToComparableNumber(b.issuedAt);
      if (aDate && bDate && aDate !== bDate) return bDate - aDate;
      if (!aDate && bDate) return 1;
      if (aDate && !bDate) return -1;
      return a.title.localeCompare(b.title, ZH_LOCALE);
    }

    if (sortBy === 'relevance') {
      const scoreDiff = computeRelevance(b, keyword) - computeRelevance(a, keyword);
      if (scoreDiff !== 0) return scoreDiff;
    }

    const dateDiff = dateToComparableNumber(b.issuedAt) - dateToComparableNumber(a.issuedAt);
    if (sortBy === 'issuedAtDesc' || sortBy === 'relevance') {
      if (dateDiff !== 0) return dateDiff;
    }
    if (sortBy === 'issuedAtAsc' && dateDiff !== 0) {
      return -dateDiff;
    }

    return a.title.localeCompare(b.title, ZH_LOCALE);
  });
}

export function policiesInProvince(policies: Policy[], adcode: string): Policy[] {
  return policies.filter((policy) => {
    if (policy.level === 'national') {
      return true;
    }
    if (policy.level === 'park' || policy.level === 'provincial' || policy.level === 'city') {
      return resolveProvinceAdcodeLoose(policy.province) === adcode;
    }
    return false;
  });
}
